import { useEffect, useMemo, useState } from 'react';
import {
  DatabaseIcon,
  GlobeIcon,
  MapPinIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
} from 'lucide-react';
import type { Provider } from '@kennisbank/shared';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from '../api/client.js';
import { Layout } from '../components/Layout.js';
import { EnrichmentDiffModal } from '../components/EnrichmentDiffModal.js';
import { SortableTh, toggleSort } from '../components/SortableTh.js';
import { InlineSpecialisationsCell } from '../components/InlineSpecialisationsCell.js';
import {
  EMPTY_PROVIDER_FORM,
  ProviderFormDialog,
  formToPayload,
  providerToForm,
} from '../components/ProviderFormDialog.js';
import { typeLabel } from './SocialeKaart.js';

/**
 * Admin Sociale Kaart manager (slice 2a). Status tab pills, "Nieuwe
 * aanbieder" CTA, table list, edit dialog. The dialog itself + all its
 * helpers live in `components/ProviderFormDialog.tsx` so the practitioner
 * page can mount the same edit affordance for admin-clicked cards.
 *
 * RBAC: route is mounted behind RoleGuard for `suppohrt_admin`. Backend
 * also re-checks via inline `req.auth.role` per A1.
 */

type ProviderListResult = { items: Provider[]; nextCursor?: string };

const STATUS_OPTIONS: Provider['status'][] = ['active', 'closed', 'unknown'];

// Specialisaties is intentionally NOT sortable — it's a free-form set; sort
// would either pick the first item alphabetically (misleading) or sort by
// length (meaningless). The Wachttijd column it replaces was sortable but
// the data was sparse (~30% non-null) so the sort was rarely useful.
type SortColumn = 'name' | 'city' | 'type' | 'lastVerifiedAt';

export function BeheerSocialeKaartPage(): JSX.Element {
  const [items, setItems] = useState<Provider[]>([]);
  const [statusFilter, setStatusFilter] = useState<Provider['status']>('active');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cursor-paginated list — server returns up to 100 per page + nextCursor.
  // The previous hard `limit: 100` cut the listing off around the letter G
  // when active providers exceeded 100 (Linda's catalogue is ~370).
  const [cursor, setCursor] = useState<string | undefined>();
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [editing, setEditing] = useState<Provider | null>(null);
  const [enriching, setEnriching] = useState<Provider | null>(null);
  const [deleting, setDeleting] = useState<Provider | null>(null);
  const [creating, setCreating] = useState(false);
  const [reload, setReload] = useState(0);
  // Tracks which provider id is mid-PATCH so the corresponding cell shows
  // "saving" state and ignores further input until the round-trip lands.
  const [savingId, setSavingId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortColumn>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  // Bulk-select voor het in één keer deactiveren van niet-passende (vooral
  // LVVP-)records. Deactiveren = status=closed; de backend lockt LVVP-records
  // erbij zodat de wekelijkse crawler ze niet reanimeert.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const sortedItems = useMemo(
    () => sortProviders(items, sortBy, sortDir),
    [items, sortBy, sortDir],
  );

  // Reset pagination whenever filters change or the page is reloaded.
  useEffect(() => {
    setCursor(undefined);
  }, [statusFilter, reload]);

  useEffect(() => {
    let cancelled = false;
    const isInitial = cursor === undefined;
    if (isInitial) setLoading(true);
    else setLoadingMore(true);
    setError(null);
    apiGet<ProviderListResult>('/api/v1/providers', {
      status: statusFilter,
      limit: 100,
      cursor,
    })
      .then((res) => {
        if (cancelled) return;
        // First page replaces; subsequent pages append.
        setItems((prev) => (cursor === undefined ? res.items : [...prev, ...res.items]));
        setNextCursor(res.nextCursor);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Kon providers niet laden');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [statusFilter, reload, cursor]);

  const refresh = (): void => setReload((n) => n + 1);

  // Clear selection whenever the visible set changes (filter switch / reload).
  useEffect(() => setSelected(new Set()), [statusFilter, reload]);

  const toggleSelect = (id: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allVisibleSelected = sortedItems.length > 0 && sortedItems.every((p) => selected.has(p.id));
  const toggleSelectAll = (): void =>
    setSelected(allVisibleSelected ? new Set() : new Set(sortedItems.map((p) => p.id)));

  // Bulk-deactivate: loop the existing per-record /status endpoint. ponytail:
  // client-side loop over a handful of records; add a server bulk endpoint only
  // if Linda routinely removes hundreds at once (N round-trips would then drag).
  const bulkDeactivate = async (): Promise<void> => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBulkBusy(true);
    setError(null);
    let failed = 0;
    for (const id of ids) {
      try {
        await apiPost(`/api/v1/providers/${id}/status`, { status: 'closed' });
      } catch {
        failed += 1;
      }
    }
    setBulkBusy(false);
    setSelected(new Set());
    if (failed > 0) setError(`${failed} van ${ids.length} niet gedeactiveerd. Probeer opnieuw.`);
    refresh();
  };

  // Inline-edit save for the Specialisaties cell. Optimistic-update keeps
  // the chip cell snappy under the round-trip; on failure we surface the
  // ApiError message in the page-level error banner and roll back.
  const patchProvider = async (id: string, patch: Partial<Provider>): Promise<void> => {
    setSavingId(id);
    setError(null);
    const prev = items;
    setItems((cur) => cur.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    try {
      const updated = await apiPatch<Provider>(`/api/v1/providers/${id}`, patch);
      setItems((cur) => cur.map((p) => (p.id === id ? updated : p)));
    } catch (err) {
      setItems(prev);
      setError(err instanceof ApiError ? err.message : 'Opslaan mislukt. Probeer opnieuw.');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Layout>
      {/* Mobile gate — admin table flow + create form aren't usable below
          ~768px. Surface a clear message instead of a broken layout. */}
      <div className="flex h-full items-center justify-center px-6 md:hidden">
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-semibold text-ink-900">
            Beheer is alleen op desktop beschikbaar
          </h1>
          <p className="mt-2 text-sm text-ink-700">
            Het beheren van zorgaanbieders werkt het beste op een laptop of tablet in
            landschapmodus. Open deze pagina opnieuw vanaf een groter scherm.
          </p>
        </div>
      </div>
      <div className="thin-scroll hidden h-full overflow-y-auto md:block">
        <div className="mx-auto max-w-6xl px-6 py-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-1 rounded-lg bg-brand-50 p-0.5 text-sm">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={`rounded-md px-3 py-1.5 capitalize transition-colors ${
                    statusFilter === s
                      ? 'bg-white text-ink-900 shadow-sm'
                      : 'text-ink-900/60 hover:text-ink-900'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-brand-700"
            >
              <PlusIcon size={14} /> Nieuwe aanbieder
            </button>
          </div>

          {error && (
            <p className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
          )}

          {selected.size > 0 && (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-brand-200 bg-brand-50/60 px-4 py-2.5 text-sm">
              <span className="text-ink-900/80">
                <strong className="text-ink-900">{selected.size}</strong> geselecteerd
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  className="rounded-md px-3 py-1.5 text-xs font-medium text-ink-800/70 hover:text-ink-900"
                >
                  Wis selectie
                </button>
                <button
                  type="button"
                  disabled={bulkBusy}
                  onClick={() => {
                    if (
                      window.confirm(
                        `${selected.size} aanbieder(s) deactiveren? Ze verdwijnen uit de ` +
                          `publieke Sociale Kaart. LVVP-records komen niet terug bij de ` +
                          `volgende crawl. Je kunt ze later weer op 'active' zetten.`,
                      )
                    ) {
                      void bulkDeactivate();
                    }
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Trash2Icon size={13} />
                  {bulkBusy ? 'Bezig…' : 'Deactiveer geselecteerde'}
                </button>
              </div>
            </div>
          )}

          {/* `overflow-visible` so inline-edit dropdowns (Specialisaties +tag)
              on the last visible row aren't clipped by the card's bottom
              edge. Same fix as Documenten. The thead carries its own
              rounded-top so the brand-50 bg still respects the card's
              rounded corners. */}
          {/* overflow-x-auto zodat de 8-koloms tabel op smallere desktops niet
              buiten het kader spilt. ponytail: de inline "+tag"-popover op de
              laatste rij kan hierdoor een verticale scroll triggeren i.p.v.
              vrij over de rand hangen — acceptabel; upgrade = popover in een
              portal als dat gaat storen. */}
          <div className="overflow-x-auto rounded-xl border border-brand-100 bg-white shadow-card">
            <table className="min-w-full text-sm">
              <thead className="rounded-t-xl bg-brand-50/50 text-left text-[11px] uppercase tracking-wider text-ink-800/60">
                <tr>
                  <th className="px-4 py-3 w-9">
                    <input
                      type="checkbox"
                      aria-label="Selecteer alle zichtbare aanbieders"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAll}
                      className="h-3.5 w-3.5 cursor-pointer accent-brand-600"
                    />
                  </th>
                  <SortableTh<SortColumn>
                    label="Naam"
                    column="name"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                  />
                  <SortableTh<SortColumn>
                    label="Plaats / provincie"
                    column="city"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                  />
                  <SortableTh<SortColumn>
                    label="Type"
                    column="type"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                  />
                  {/* Specialisaties replaces the old Wachttijd column —
                      Wachttijd was sparse (~30% non-null) and not editable
                      from this table. Specialisaties is editable inline
                      via the same chip-picker as the Documenten admin
                      table. Not sortable (free-form set). */}
                  <th className="px-4 py-3">Specialisaties</th>
                  <SortableTh<SortColumn>
                    label="Geverifieerd"
                    column="lastVerifiedAt"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={(c) => toggleSort(c, sortBy, setSortBy, sortDir, setSortDir)}
                  />
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-50">
                {sortedItems.map((p) => (
                  <tr key={p.id} className="hover:bg-brand-50/40">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        aria-label={`Selecteer ${p.name}`}
                        checked={selected.has(p.id)}
                        onChange={() => toggleSelect(p.id)}
                        className="h-3.5 w-3.5 cursor-pointer accent-brand-600"
                      />
                    </td>
                    <td className="px-4 py-3 font-medium text-ink-900">
                      {p.name}
                      {p.lvvpStale && (
                        <span
                          className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800"
                          title={
                            p.lvvpLastSeenAt
                              ? `Laatst gevonden in LVVP op ${formatDate(p.lvvpLastSeenAt)}`
                              : undefined
                          }
                        >
                          Niet meer in LVVP
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-900/70">
                      <span className="flex items-start gap-1.5">
                        <MapPinIcon
                          size={12}
                          aria-hidden="true"
                          className="mt-0.5 h-3 w-3 flex-shrink-0 text-brand-700/60"
                        />
                        <span>
                          {[p.location.city, p.location.province].filter(Boolean).join(', ')}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink-900/70">{typeLabel(p.type)}</td>
                    <td className="px-4 py-3">
                      <InlineSpecialisationsCell
                        value={p.specialisations}
                        saving={savingId === p.id}
                        onSave={(v) => patchProvider(p.id, { specialisations: v })}
                      />
                    </td>
                    <td className="px-4 py-3 text-ink-900/70">{formatDate(p.lastVerifiedAt)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] ${
                          p.status === 'active'
                            ? 'bg-emerald-100 text-emerald-800'
                            : p.status === 'closed'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {p.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setEnriching(p)}
                          disabled={!p.contactInfo.website}
                          aria-label={`Verrijk ${p.name} vanuit website`}
                          title={
                            p.contactInfo.website
                              ? 'Verrijk vanuit website'
                              : 'Geen website ingesteld'
                          }
                          className="inline-flex items-center justify-center rounded-md border border-brand-200 p-1.5 text-ink-800 transition-colors hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <GlobeIcon size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditing(p)}
                          aria-label={`Bewerk ${p.name}`}
                          title="Bewerk"
                          className="inline-flex items-center justify-center rounded-md border border-brand-200 p-1.5 text-ink-800 transition-colors hover:bg-brand-50"
                        >
                          <PencilIcon size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleting(p)}
                          aria-label={`Verwijder ${p.name}`}
                          title="Verwijder permanent"
                          className="inline-flex items-center justify-center rounded-md border border-red-200 p-1.5 text-red-700 transition-colors hover:bg-red-50"
                        >
                          <Trash2Icon size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-ink-900/50">
                      Geen aanbieders gevonden voor status "{statusFilter}".
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {nextCursor && (
            <div className="mt-4 flex items-center justify-between gap-3 text-xs text-ink-900/55">
              <span>
                {items.length} aanbieders geladen
                {loadingMore && ' — laden…'}
              </span>
              <button
                type="button"
                onClick={() => setCursor(nextCursor)}
                disabled={loadingMore}
                className="rounded-md border border-brand-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-800 transition-colors hover:bg-brand-50 disabled:opacity-60"
              >
                {loadingMore ? 'Laden…' : 'Toon meer'}
              </button>
            </div>
          )}

          <p className="mt-4 flex items-center gap-1.5 text-[11px] text-ink-900/50">
            <DatabaseIcon size={11} /> Elke wijziging wordt vastgelegd in het audit log.
          </p>
        </div>
      </div>

      {creating && (
        <ProviderFormDialog
          title="Nieuwe aanbieder"
          initial={EMPTY_PROVIDER_FORM}
          mode="create"
          onClose={() => setCreating(false)}
          onSubmit={async (form) => {
            await apiPost('/api/v1/providers', formToPayload(form));
            setCreating(false);
            refresh();
          }}
        />
      )}

      {enriching && (
        <EnrichmentDiffModal
          provider={enriching}
          onClose={() => setEnriching(null)}
          onApplied={() => {
            setEnriching(null);
            refresh();
          }}
        />
      )}

      {deleting && (
        <DeleteConfirmDialog
          provider={deleting}
          onCancel={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            refresh();
          }}
        />
      )}

      {editing && (
        <ProviderFormDialog
          title={`Bewerk: ${editing.name}`}
          initial={providerToForm(editing)}
          mode="edit"
          onClose={() => setEditing(null)}
          onSubmit={async (form, opts) => {
            // Strip `source` from the PATCH: the update-route schema doesn't
            // accept 'lvvp', and source never changes on edit anyway - the
            // backend preserves the existing value (and reads it for the
            // LVVP auto-lock) via the merge in ProviderService.update.
            const {
              lastVerifiedAt: _omit,
              source: _omitSource,
              ...patch
            } = formToPayload(form) as Record<string, unknown> & { lastVerifiedAt: string };
            void _omit;
            void _omitSource;
            await apiPatch(
              `/api/v1/providers/${editing.id}`,
              patch,
              opts.bumpVerified ? undefined : { bumpVerified: 'false' },
            );
            setEditing(null);
            refresh();
          }}
        />
      )}
    </Layout>
  );
}

function DeleteConfirmDialog({
  provider,
  onCancel,
  onDeleted,
}: {
  provider: Provider;
  onCancel: () => void;
  onDeleted: () => void;
}): JSX.Element {
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const matches = confirmText.trim() === provider.name;

  async function submit(): Promise<void> {
    if (!matches) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiDelete(`/api/v1/providers/${provider.id}`, undefined, {
        'x-confirm-name': provider.name,
      });
      onDeleted();
    } catch (err) {
      setSubmitting(false);
      setError(err instanceof ApiError ? err.message : 'Verwijderen mislukt. Probeer opnieuw.');
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
    >
      <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
        <div className="border-b border-red-100 px-6 py-4">
          <h2 id="delete-title" className="text-lg font-semibold text-red-700">
            Provider verwijderen?
          </h2>
        </div>
        <div className="px-6 py-4">
          <p className="text-sm text-ink-900/80">
            Dit verwijdert <span className="font-semibold">{provider.name}</span>
            {provider.location.city ? ` (${provider.location.city})` : ''} permanent uit de Sociale
            Kaart. Niet ongedaan te maken.
          </p>
          <p className="mt-3 text-xs text-ink-900/60">
            Tip: gebruik &ldquo;status = closed&rdquo; via Bewerk als je de provider alleen wilt
            verbergen voor gebruikers.
          </p>
          <label className="mt-4 block text-xs font-medium text-ink-900/70">
            Typ &ldquo;{provider.name}&rdquo; om te bevestigen
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoFocus
            className="mt-1 w-full rounded-md border border-brand-200 px-3 py-1.5 text-sm focus:border-red-400 focus:outline-none"
          />
          {error && <p className="mt-2 text-xs text-red-700">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-brand-100 bg-brand-50/30 px-6 py-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-md border border-brand-200 px-4 py-1.5 text-sm text-ink-900 transition-colors hover:bg-brand-50 disabled:opacity-50"
          >
            Annuleer
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!matches || submitting}
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Trash2Icon size={14} />
            {submitting ? 'Bezig…' : 'Verwijder permanent'}
          </button>
        </div>
      </div>
    </div>
  );
}

function sortProviders(items: Provider[], by: SortColumn, dir: 'asc' | 'desc'): Provider[] {
  const sign = dir === 'asc' ? 1 : -1;
  const out = [...items];
  out.sort((a, b) => {
    let cmp = 0;
    switch (by) {
      case 'name':
        cmp = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        break;
      case 'city':
        cmp = (a.location.city ?? '')
          .toLowerCase()
          .localeCompare((b.location.city ?? '').toLowerCase());
        break;
      case 'type':
        cmp = a.type.localeCompare(b.type);
        break;
      case 'lastVerifiedAt':
        cmp = a.lastVerifiedAt.localeCompare(b.lastVerifiedAt);
        break;
    }
    return cmp * sign;
  });
  return out;
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
